/**
 * Utilities for dynamic form field keys.
 * Conditional child fields are often stored as: "{childFieldName} {parentFieldName}"
 */

export const buildCompositeFieldKey = (childFieldName, parentFieldName) =>
  `${childFieldName} ${parentFieldName}`;

export const parseCompositeFieldKey = (fieldKey) => {
  if (!fieldKey || typeof fieldKey !== 'string') {
    return null;
  }

  const spaceIndex = fieldKey.indexOf(' ');
  if (spaceIndex <= 0) {
    return null;
  }

  return {
    childFieldName: fieldKey.slice(0, spaceIndex),
    parentFieldName: fieldKey.slice(spaceIndex + 1),
  };
};

export const getConditionalFieldValue = (
  fields = {},
  childFieldName,
  parentFieldName
) => {
  if (!childFieldName || !fields) {
    return undefined;
  }

  if (parentFieldName) {
    const compositeKey = buildCompositeFieldKey(childFieldName, parentFieldName);
    const compositeValue = fields[compositeKey];
    if (
      compositeValue !== undefined &&
      compositeValue !== null &&
      compositeValue !== ''
    ) {
      return compositeValue;
    }
  }

  return fields[childFieldName];
};

export const findFormFieldDefinition = (formFieldsSections = {}, fieldName) => {
  if (!fieldName) {
    return null;
  }

  let matchedField = null;
  Object.values(formFieldsSections || {}).forEach((sectionFields) => {
    (sectionFields || []).forEach((field) => {
      if (field?.field_name === fieldName) {
        matchedField = field;
      }
    });
  });

  return matchedField;
};

export const collectConditionalChildNamesFromOptions = (parentOptions) => {
  const childNames = new Set();
  const options = Array.isArray(parentOptions)
    ? parentOptions
    : parseFieldOptions(parentOptions);

  options.forEach((optionItem) => {
    const conditionalField = Object.values(optionItem || {})?.[0];
    if (!conditionalField?.field_name) {
      return;
    }

    childNames.add(conditionalField.field_name);

    if (conditionalField.option) {
      collectConditionalChildNamesFromOptions(conditionalField.option).forEach(
        (nestedName) => childNames.add(nestedName)
      );
    }
  });

  return childNames;
};

const normalizeOptionToken = (value) => String(value ?? '').trim();

/** Normalize comma-joined checkbox storage so labels that contain commas still match. */
const normalizeCheckboxCsv = (value) =>
  String(value ?? '')
    .replace(/\s*,\s*/g, ',')
    .replace(/,+/g, ',')
    .replace(/^,|,$/g, '');

const uniqueTrimmedValues = (items = []) => {
  const seen = new Set();
  const result = [];
  items.forEach((item) => {
    const value = normalizeOptionToken(item);
    if (!value || seen.has(value)) {
      return;
    }
    seen.add(value);
    result.push(value);
  });
  return result;
};

/**
 * Parse saved checkbox values without naive comma-splitting.
 * Option labels like "Ventilated, adequate natural light..." contain commas,
 * so matching must use known option keys (longest-first / delimited includes).
 */
export const parseStoredCheckboxValues = (saved, optionKeys = []) => {
  if (Array.isArray(saved)) {
    return uniqueTrimmedValues(saved);
  }

  const raw = normalizeOptionToken(saved);
  if (!raw) {
    return [];
  }

  const keys = uniqueTrimmedValues(optionKeys);
  if (!keys.length) {
    return uniqueTrimmedValues(raw.split(','));
  }

  const haystack = `,${normalizeCheckboxCsv(raw)},`;
  const matched = keys.filter((key) =>
    haystack.includes(`,${normalizeCheckboxCsv(key)},`)
  );

  return matched.sort(
    (left, right) =>
      haystack.indexOf(`,${normalizeCheckboxCsv(left)},`) -
      haystack.indexOf(`,${normalizeCheckboxCsv(right)},`)
  );
};

export const getCheckboxOptionKeys = (option) => {
  if (!option) {
    return [];
  }

  let options = [];
  if (typeof option === 'string') {
    try {
      options = JSON.parse(option);
    } catch (error) {
      try {
        // Legacy form builder stored JS array literals (eval-compatible).
        // eslint-disable-next-line no-eval
        options = eval(option);
      } catch (evalError) {
        return [];
      }
    }
  } else if (Array.isArray(option)) {
    options = option;
  }

  if (!Array.isArray(options)) {
    return [];
  }

  return options
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return '';
      }
      return Object.keys(item)[0] || '';
    })
    .filter(Boolean);
};

export const isCheckboxOptionSelected = (selectedValues, optionKey) => {
  const normalizedOption = normalizeOptionToken(optionKey);
  if (!normalizedOption) {
    return false;
  }

  if (Array.isArray(selectedValues)) {
    return selectedValues.some(
      (item) =>
        normalizeCheckboxCsv(item) === normalizeCheckboxCsv(normalizedOption)
    );
  }

  return parseStoredCheckboxValues(selectedValues, [normalizedOption]).includes(
    normalizedOption
  );
};

/**
 * Fallback splitter for numbered checklist answers when option keys are unknown.
 * Splits on boundaries like ",2. " / ",3.2 " without breaking commas inside an item.
 */
export const splitNumberedListDisplayValues = (saved) => {
  const raw = normalizeOptionToken(saved).replace(/,$/, '');
  if (!raw) {
    return [];
  }

  const parts = raw.split(/,(?=\s*\d+(?:\.\d+)?(?:\.|\s))/);
  return uniqueTrimmedValues(parts);
};

export const resolveFormFieldDefinition = (
  formFieldsSections = {},
  fieldKey
) => {
  if (!fieldKey) {
    return null;
  }

  const definitions = collectFormFieldDefinitions(formFieldsSections);
  const direct = definitions.find((field) => field?.field_name === fieldKey);
  if (direct) {
    return direct;
  }

  const parsed = parseCompositeFieldKey(fieldKey);
  if (!parsed) {
    return findFormFieldDefinition(formFieldsSections, fieldKey);
  }

  return (
    definitions.find((field) => field?.field_name === parsed.childFieldName) ||
    findFormFieldDefinition(formFieldsSections, parsed.childFieldName)
  );
};

/**
 * Break checkbox / numbered multi-select answers into compact display lines for view mode.
 */
export const getResponseValueDisplayLines = (
  value,
  formFieldsSections = {},
  fieldKey
) => {
  if (value === null || value === undefined || value === '') {
    return [];
  }

  if (Array.isArray(value)) {
    return uniqueTrimmedValues(value);
  }

  if (typeof value !== 'string') {
    return [String(value)];
  }

  const definition = resolveFormFieldDefinition(formFieldsSections, fieldKey);
  const optionKeys = getCheckboxOptionKeys(definition?.option);
  const isCheckboxField = definition?.field_type === 'checkbox';

  if (isCheckboxField || optionKeys.length > 1) {
    const parsed = parseStoredCheckboxValues(value, optionKeys);
    if (parsed.length > 1 || (isCheckboxField && parsed.length === 1)) {
      return parsed;
    }
  }

  const numbered = splitNumberedListDisplayValues(value);
  if (numbered.length > 1) {
    return numbered;
  }

  return [normalizeOptionToken(value)];
};

const isParentOptionActive = (parentValue, optionKey) => {
  if (parentValue === undefined || parentValue === null || parentValue === '') {
    return false;
  }

  const normalizedOption = normalizeOptionToken(optionKey);

  if (Array.isArray(parentValue)) {
    return parentValue.some(
      (item) =>
        normalizeCheckboxCsv(item) === normalizeCheckboxCsv(normalizedOption)
    );
  }

  const haystack = `,${normalizeCheckboxCsv(parentValue)},`;
  if (haystack.includes(`,${normalizeCheckboxCsv(normalizedOption)},`)) {
    return true;
  }

  const normalizedParentValue = normalizeOptionToken(parentValue);
  return normalizedParentValue === normalizedOption;
};

const isIndexedStorageKey = (key) => {
  if (!key || typeof key !== 'string') {
    return false;
  }

  const parts = key.split(' ');
  return parts.length >= 2 && /^\d+$/.test(parts[parts.length - 1]);
};

const stripIndexSuffix = (key) => {
  const parts = String(key).split(' ');
  return parts.slice(0, -1).join(' ');
};

/**
 * Copy "field_name 12" / "child parent 12" keys onto canonical names used by edit controls.
 */
export const remapIndexedSavedFields = (fields = {}) => {
  const nextFields = { ...fields };

  Object.keys(fields).forEach((key) => {
    if (!isIndexedStorageKey(key)) {
      return;
    }

    const canonicalKey = stripIndexSuffix(key);
    if (!canonicalKey) {
      return;
    }

    if (
      !hasDisplayableValue(nextFields[canonicalKey]) &&
      hasDisplayableValue(fields[key])
    ) {
      nextFields[canonicalKey] = fields[key];
    }

    delete nextFields[key];
  });

  return nextFields;
};

export const parseStoredDateForInput = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }

  const slashOrDash = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (slashOrDash) {
    const day = slashOrDash[1].padStart(2, '0');
    const month = slashOrDash[2].padStart(2, '0');
    const year = slashOrDash[3];
    return `${year}-${month}-${day}`;
  }

  return '';
};

export const lookupSavedFieldValue = (
  fields = {},
  fieldName,
  parentFieldName
) => {
  if (!fieldName || !fields) {
    return undefined;
  }

  const merged = parseResponseFields(fields);
  const conditionalValue = getConditionalFieldValue(
    merged,
    fieldName,
    parentFieldName
  );
  if (hasDisplayableValue(conditionalValue)) {
    return conditionalValue;
  }

  const indexedMatch = Object.keys(merged).find((key) => {
    if (!isIndexedStorageKey(key) || !hasDisplayableValue(merged[key])) {
      return false;
    }
    const withoutIndex = stripIndexSuffix(key);
    return withoutIndex === fieldName || key.startsWith(`${fieldName} `);
  });

  if (indexedMatch) {
    return merged[indexedMatch];
  }

  return merged[fieldName];
};

export const collectKnownSavedFieldKeys = (formFieldsSections = {}) => {
  const keys = new Set();

  Object.values(formFieldsSections || {}).forEach((sectionFields) => {
    (sectionFields || []).forEach((field, fieldIndex) => {
      if (isHeadingFieldType(field?.field_type)) {
        keys.add(getStaticHeadingStorageKey(field, fieldIndex));
        return;
      }

      if (field?.field_name) {
        keys.add(field.field_name);
      }

      parseFieldOptions(field?.option).forEach((optionItem, optionIndex) => {
        const conditionalField = getConditionalFieldFromOptionItem(optionItem);
        if (!conditionalField) {
          return;
        }

        if (isHeadingFieldType(conditionalField.field_type)) {
          keys.add(getHeadingStorageKey(conditionalField, optionIndex));
          return;
        }

        if (!conditionalField.field_name) {
          return;
        }

        keys.add(conditionalField.field_name);
        if (field?.field_name) {
          keys.add(
            buildCompositeFieldKey(conditionalField.field_name, field.field_name)
          );
        }
      });
    });
  });

  return keys;
};

export const isMappedSavedFieldKey = (key, knownKeys = new Set()) => {
  if (!key) {
    return true;
  }
  if (knownKeys.has(key) || isHeadingFieldKey(key)) {
    return true;
  }
  if (isIndexedStorageKey(key)) {
    return isMappedSavedFieldKey(stripIndexSuffix(key), knownKeys);
  }

  const parsed = parseCompositeFieldKey(key);
  if (!parsed) {
    return false;
  }

  return (
    knownKeys.has(parsed.childFieldName) ||
    knownKeys.has(parsed.parentFieldName)
  );
};

/**
 * Remove conditional child values for branches that are not active
 * for the current parent field value (radio/checkbox with options).
 */
export const clearStaleConditionalFieldsForParent = (
  fields = {},
  parentFieldName,
  parentOptions
) => {
  const nextFields = { ...fields };
  const options = Array.isArray(parentOptions)
    ? parentOptions
    : parseFieldOptions(parentOptions);
  const parentValue = nextFields[parentFieldName];

  options.forEach((optionItem) => {
    const optionKey = Object.keys(optionItem || {})?.[0];
    const conditionalField = Object.values(optionItem || {})?.[0];
    if (!optionKey || !conditionalField?.field_name) {
      return;
    }

    if (isParentOptionActive(parentValue, optionKey)) {
      return;
    }

    const childName = conditionalField.field_name;
    delete nextFields[childName];
    delete nextFields[buildCompositeFieldKey(childName, parentFieldName)];
  });

  Object.keys(nextFields).forEach((key) => {
    const parsed = parseCompositeFieldKey(key);
    if (parsed?.parentFieldName === parentFieldName) {
      const optionForChild = options.find((optionItem) => {
        const conditionalField = Object.values(optionItem || {})?.[0];
        return conditionalField?.field_name === parsed.childFieldName;
      });
      const optionKey = optionForChild ? Object.keys(optionForChild)[0] : null;
      if (!optionKey || !isParentOptionActive(parentValue, optionKey)) {
        delete nextFields[key];
      }
    }
  });

  return nextFields;
};

export const applyParentFieldUpdate = (
  fields = {},
  parentFieldName,
  parentValue,
  parentOptions
) => {
  const clearedFields = clearStaleConditionalFieldsForParent(
    fields,
    parentFieldName,
    parentOptions
  );

  return {
    ...clearedFields,
    [parentFieldName]: parentValue,
  };
};

/**
 * Drop conditional values whose parent option is no longer selected.
 * Use before save and when normalizing saved responses for display.
 */
export const pruneInactiveConditionalFields = (fields = {}, formFieldsSections = {}) => {
  let prunedFields = { ...fields };

  Object.values(formFieldsSections || {}).forEach((sectionFields) => {
    (sectionFields || []).forEach((parentField) => {
      if (!parentField?.field_name || !parentField?.option) {
        return;
      }

      prunedFields = clearStaleConditionalFieldsForParent(
        prunedFields,
        parentField.field_name,
        parentField.option
      );
    });
  });

  return prunedFields;
};

export const applyDynamicFormFieldUpdate = (fields = {}, fieldKey, value) => {
  const nextFields = {
    ...fields,
    [fieldKey]: value,
  };

  const parsed = parseCompositeFieldKey(fieldKey);
  if (
    parsed?.childFieldName &&
    Object.prototype.hasOwnProperty.call(nextFields, parsed.childFieldName)
  ) {
    delete nextFields[parsed.childFieldName];
  }

  return nextFields;
};

/**
 * Merge original saved fields with in-session edits.
 * Removes stale base keys when a composite conditional key was updated.
 */
export const mergeEditFormFields = (
  originalFields = {},
  updatedFields = {},
  formFieldsSections = {}
) => {
  const merged = remapIndexedSavedFields({
    ...originalFields,
    ...updatedFields,
  });

  Object.keys(updatedFields).forEach((key) => {
    const parsed = parseCompositeFieldKey(key);
    if (
      parsed?.childFieldName &&
      Object.prototype.hasOwnProperty.call(merged, parsed.childFieldName)
    ) {
      delete merged[parsed.childFieldName];
    }
  });

  const prunedFields = pruneInactiveConditionalFields(merged, formFieldsSections);
  return syncConditionalHeadingFieldsForFields(prunedFields, formFieldsSections);
};

export const isHeadingFieldKey = (fieldKey) => {
  if (!fieldKey || typeof fieldKey !== 'string') {
    return false;
  }
  return (
    fieldKey === 'headings' ||
    fieldKey === 'text_headings' ||
    fieldKey === 'sub_headings' ||
    fieldKey.startsWith('headings_') ||
    fieldKey.startsWith('text_headings_') ||
    fieldKey.startsWith('sub_headings_')
  );
};

const isHeadingFieldType = (fieldType) =>
  fieldType === 'headings' ||
  fieldType === 'text_headings' ||
  fieldType === 'sub_headings';

const getConditionalFieldFromOptionItem = (optionItem) => {
  if (!optionItem || typeof optionItem !== 'object') {
    return null;
  }

  const conditionalField = Object.values(optionItem)[0];
  return conditionalField && typeof conditionalField === 'object'
    ? conditionalField
    : null;
};

const getHeadingStorageKey = (conditionalField, optionIndex) =>
  `${conditionalField.field_type}_${optionIndex}`;

const isHeadingOptionActive = (parentValue, optionItem, conditionalField) => {
  const optionKey = Object.keys(optionItem || {})[0];
  if (optionKey && isParentOptionActive(parentValue, optionKey)) {
    return true;
  }

  if (conditionalField?.option_key) {
    return isParentOptionActive(parentValue, conditionalField.option_key);
  }

  return false;
};

/**
 * Immutably add/remove conditional heading keys in a flat fields map (edit mode).
 */
export const syncConditionalHeadingFieldsForFields = (
  fields = {},
  formFieldsSections = {}
) => {
  const nextFields = { ...fields };

  Object.values(formFieldsSections || {}).forEach((sectionFields) => {
    (sectionFields || []).forEach((parentField) => {
      if (!parentField?.option) {
        return;
      }

      const parentValue = nextFields[parentField.field_name];
      const options = parseFieldOptions(parentField.option);

      options.forEach((optionItem, optionIndex) => {
        const conditionalField = getConditionalFieldFromOptionItem(optionItem);
        if (!conditionalField || !isHeadingFieldType(conditionalField.field_type)) {
          return;
        }

        const headingKey = getHeadingStorageKey(conditionalField, optionIndex);

        if (isHeadingOptionActive(parentValue, optionItem, conditionalField)) {
          nextFields[headingKey] = conditionalField.field_label;
        } else {
          delete nextFields[headingKey];
        }
      });
    });
  });

  return nextFields;
};

/**
 * Immutably add/remove conditional heading keys in sectioned form state (new form).
 */
export const syncConditionalHeadingFieldsForForm = (
  form = {},
  formFieldsSections = {}
) => {
  const nextForm = Object.keys(form || {}).reduce((acc, sectionKey) => {
    acc[sectionKey] = { ...(form[sectionKey] || {}) };
    return acc;
  }, {});

  Object.values(formFieldsSections || {}).forEach((sectionFields) => {
    (sectionFields || []).forEach((parentField) => {
      if (!parentField?.option) {
        return;
      }

      const options = parseFieldOptions(parentField.option);

      options.forEach((optionItem, optionIndex) => {
        const conditionalField = getConditionalFieldFromOptionItem(optionItem);
        if (!conditionalField || !isHeadingFieldType(conditionalField.field_type)) {
          return;
        }

        const headingKey = getHeadingStorageKey(conditionalField, optionIndex);

        Object.keys(nextForm).forEach((sectionKey) => {
          const sectionValues = nextForm[sectionKey];
          const parentValue = sectionValues[parentField.field_name];

          if (isHeadingOptionActive(parentValue, optionItem, conditionalField)) {
            sectionValues[headingKey] = conditionalField.field_label;
          } else {
            delete sectionValues[headingKey];
          }
        });
      });
    });
  });

  return nextForm;
};

export const areFieldMapsEqual = (left = {}, right = {}) => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((key) => left[key] === right[key]);
};

/**
 * Normalize saved edit payload once form definitions and response are both available.
 */
export const hydrateEditFormFields = (fields = {}, formFieldsSections = {}) =>
  syncConditionalHeadingFieldsForFields(
    pruneInactiveConditionalFields(
      remapIndexedSavedFields(parseResponseFields(fields)),
      formFieldsSections
    ),
    formFieldsSections
  );

export const areSectionedFormsEqual = (left = {}, right = {}) => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((sectionKey) =>
    areFieldMapsEqual(left[sectionKey], right[sectionKey])
  );
};

export const formatFieldKeyAsFallbackLabel = (fieldKey) => {
  if (!fieldKey) {
    return '';
  }
  const parsed = parseCompositeFieldKey(fieldKey);
  const displayKey = parsed?.childFieldName || fieldKey;
  return displayKey.split('_').join(' ');
};

const parseFieldOptions = (option) => {
  if (!option) {
    return [];
  }
  if (typeof option === 'string') {
    try {
      return JSON.parse(option);
    } catch (error) {
      return [];
    }
  }
  return Array.isArray(option) ? option : [];
};

export const collectFormFieldDefinitions = (formFieldsSections = {}) => {
  const definitions = [];

  Object.values(formFieldsSections || {}).forEach((sectionFields) => {
    (sectionFields || []).forEach((field) => {
      if (!field?.field_name) {
        return;
      }

      definitions.push(field);

      parseFieldOptions(field.option).forEach((optionItem) => {
        Object.values(optionItem || {}).forEach((conditionalField) => {
          if (
            conditionalField &&
            typeof conditionalField === 'object' &&
            conditionalField.field_name
          ) {
            definitions.push({
              ...conditionalField,
              parent_field_name: field.field_name,
            });
          }
        });
      });
    });
  });

  return definitions;
};

const getStaticHeadingStorageKey = (field, fieldIndex) =>
  `${field.field_type}_${fieldIndex}`;

const hasDisplayableValue = (value) =>
  value !== null && value !== undefined && value !== '';

const shouldSkipBaseFieldKeyForDisplay = (fieldKey, fields = {}) => {
  const parsed = parseCompositeFieldKey(fieldKey);
  if (parsed) {
    return false;
  }

  return Object.keys(fields).some((key) => {
    const composite = parseCompositeFieldKey(key);
    return (
      composite?.childFieldName === fieldKey &&
      hasDisplayableValue(fields[key])
    );
  });
};

/**
 * Walk sections in the same order DynamicForm uses when binding fields.
 * Static headings use `${field_type}_${index}`; conditionals use composite keys.
 */
const buildEditLayoutResponseEntries = (displayFields, formFieldsSections, labelMap) => {
  const entries = [];
  const usedKeys = new Set();

  const pushEntry = (key, { forceHeading = null } = {}) => {
    if (!key || usedKeys.has(key)) {
      return;
    }

    const isHeading = forceHeading ?? isHeadingFieldKey(key);
    let value = displayFields[key];

    if (!hasDisplayableValue(value)) {
      if (isHeading && labelMap[key]) {
        value = labelMap[key];
      } else {
        return;
      }
    }

    if (!isHeading && shouldSkipBaseFieldKeyForDisplay(key, displayFields)) {
      return;
    }

    usedKeys.add(key);
    entries.push({
      key,
      value,
      label: isHeading ? value : getResponseFieldLabel(key, labelMap),
      isHeading,
    });
  };

  Object.values(formFieldsSections || {}).forEach((sectionFields) => {
    (sectionFields || []).forEach((field, fieldIndex) => {
      if (isHeadingFieldType(field?.field_type)) {
        pushEntry(getStaticHeadingStorageKey(field, fieldIndex), { forceHeading: true });
        return;
      }

      if (!field?.field_name) {
        return;
      }

      pushEntry(field.field_name);

      const parentValue = displayFields[field.field_name];
      const options = parseFieldOptions(field.option);

      options.forEach((optionItem, optionIndex) => {
        const conditionalField = getConditionalFieldFromOptionItem(optionItem);
        if (!conditionalField) {
          return;
        }

        if (!isHeadingOptionActive(parentValue, optionItem, conditionalField)) {
          return;
        }

        if (isHeadingFieldType(conditionalField.field_type)) {
          pushEntry(getHeadingStorageKey(conditionalField, optionIndex), {
            forceHeading: true,
          });
          return;
        }

        if (!conditionalField.field_name) {
          return;
        }

        const compositeKey = buildCompositeFieldKey(
          conditionalField.field_name,
          field.field_name
        );

        if (Object.prototype.hasOwnProperty.call(displayFields, compositeKey)) {
          pushEntry(compositeKey);
        } else {
          pushEntry(conditionalField.field_name);
        }
      });
    });
  });

  Object.keys(displayFields).forEach((key) => {
    pushEntry(key);
  });

  return entries;
};

export const buildFieldLabelMap = (formFieldsSections = {}) => {
  const labelMap = {};

  Object.values(formFieldsSections || {}).forEach((sectionFields) => {
    (sectionFields || []).forEach((field, fieldIndex) => {
      if (isHeadingFieldType(field?.field_type)) {
        labelMap[getStaticHeadingStorageKey(field, fieldIndex)] =
          field.field_label || getStaticHeadingStorageKey(field, fieldIndex);
        return;
      }

      if (!field?.field_name) {
        return;
      }

      const label = field.field_label || field.field_name;
      labelMap[field.field_name] = label;

      parseFieldOptions(field.option).forEach((optionItem, optionIndex) => {
        const conditionalField = getConditionalFieldFromOptionItem(optionItem);
        if (!conditionalField) {
          return;
        }

        if (isHeadingFieldType(conditionalField.field_type)) {
          labelMap[getHeadingStorageKey(conditionalField, optionIndex)] =
            conditionalField.field_label ||
            getHeadingStorageKey(conditionalField, optionIndex);
          return;
        }

        const childLabel = conditionalField.field_label || conditionalField.field_name;
        labelMap[conditionalField.field_name] = childLabel;
        labelMap[buildCompositeFieldKey(conditionalField.field_name, field.field_name)] =
          childLabel;
      });
    });
  });

  collectFormFieldDefinitions(formFieldsSections).forEach((field) => {
    if (!field?.field_name) {
      return;
    }

    const label = field.field_label || field.field_name;
    labelMap[field.field_name] = label;

    if (field.parent_field_name) {
      labelMap[buildCompositeFieldKey(field.field_name, field.parent_field_name)] =
        label;
    }
  });

  return labelMap;
};

export const getResponseFieldLabel = (fieldKey, labelMap = {}) => {
  if (!fieldKey) {
    return '';
  }

  if (labelMap[fieldKey]) {
    return labelMap[fieldKey];
  }

  const parsed = parseCompositeFieldKey(fieldKey);
  if (parsed?.childFieldName && labelMap[parsed.childFieldName]) {
    return labelMap[parsed.childFieldName];
  }

  return formatFieldKeyAsFallbackLabel(fieldKey);
};

const addConditionalHeadingEntriesForParent = (
  addEntry,
  fields,
  parentField
) => {
  if (!parentField?.option) {
    return;
  }

  const parentValue = fields[parentField.field_name];
  const options = parseFieldOptions(parentField.option);

  options.forEach((optionItem, optionIndex) => {
    const conditionalField = getConditionalFieldFromOptionItem(optionItem);
    if (!conditionalField || !isHeadingFieldType(conditionalField.field_type)) {
      return;
    }

    if (isHeadingOptionActive(parentValue, optionItem, conditionalField)) {
      addEntry(getHeadingStorageKey(conditionalField, optionIndex));
    }
  });
};

export const getOrderedDisplayableResponseEntries = (
  fields = {},
  formFieldsSections = {},
  options = {}
) => {
  const {
    pruneByDefinitions = false,
    preserveResponseOrder = false,
    matchEditLayout = false,
  } = options;
  const parsedFields = parseResponseFields(fields);
  const hasFormDefinition =
    formFieldsSections && Object.keys(formFieldsSections).length > 0;
  const displayFields =
    hasFormDefinition && pruneByDefinitions
      ? hydrateEditFormFields(parsedFields, formFieldsSections)
      : parsedFields;

  const labelMap = buildFieldLabelMap(formFieldsSections);

  if (matchEditLayout && hasFormDefinition) {
    return buildEditLayoutResponseEntries(
      displayFields,
      formFieldsSections,
      labelMap
    );
  }

  if (preserveResponseOrder) {
    return Object.keys(displayFields)
      .filter((key) => hasDisplayableValue(displayFields[key]))
      .map((key) => ({
        key,
        value: displayFields[key],
        label: getResponseFieldLabel(key, labelMap),
        isHeading: isHeadingFieldKey(key),
      }));
  }

  const entries = [];
  const usedKeys = new Set();

  const addEntry = (key) => {
    if (!key || usedKeys.has(key)) {
      return;
    }
    if (!Object.prototype.hasOwnProperty.call(displayFields, key)) {
      return;
    }

    const value = displayFields[key];
    if (!hasDisplayableValue(value)) {
      return;
    }

    if (shouldSkipBaseFieldKeyForDisplay(key, displayFields)) {
      return;
    }

    usedKeys.add(key);
    entries.push({
      key,
      value,
      label: getResponseFieldLabel(key, labelMap),
      isHeading: isHeadingFieldKey(key),
    });
  };

  collectFormFieldDefinitions(formFieldsSections).forEach((field) => {
    if (field.parent_field_name) {
      addEntry(buildCompositeFieldKey(field.field_name, field.parent_field_name));
      addEntry(field.field_name);
      return;
    }

    addEntry(field.field_name);
    addConditionalHeadingEntriesForParent(addEntry, displayFields, field);
  });

  Object.keys(displayFields).forEach((key) => addEntry(key));

  return entries;
};

export const parseResponseFields = (fields) => {
  if (!fields) {
    return {};
  }
  if (typeof fields === 'string') {
    try {
      return JSON.parse(fields);
    } catch (error) {
      return {};
    }
  }
  return fields;
};
